/**
 * A run pressed while the opening is still going (D351): the strip's own Run,
 * held until the opening lands, and started then only if the opening landed
 * clean on the exploration it seeded (`chainedRunDue`).
 *
 * ONE EXPLORE, ONE RUN. A split press beside Explore — "Explore, then let it
 * run", with its own popover and its own figure — was a second door to the run,
 * which D338 had put on the strip, and a third price for it. So Explore only
 * opens, and while the opening is going the strip offers its Run as it would
 * over any graph: the same run, the same estimate on the same row. Pressed then,
 * the strip says the run is waiting on the opening, with Cancel beside it, until
 * the opening lands. A Stop, a failure or an opening that lands nothing starts
 * nothing — the user's hand and the opening's own failure honoured, as they were.
 *
 * What is held is the run as the strip priced it at the press: its spec, its move
 * count and its figure, so the row keeps saying the run that will start rather
 * than whatever the dials say by the time the opening lands.
 *
 * Module state, `composerMode.ts`'s reason: the composer that holds the press
 * need not be the one on screen when the opening lands — a phone's sheet unmounts
 * it — and a press waiting a few seconds is the reader's, never the document's.
 * Nothing here is stored, and nothing here spends: the run starts through
 * `startRun`, with every refusal it makes, from the composer's own continuation.
 */

import { useSyncExternalStore } from 'react'
import type { RunSpec } from '../engine/run'

/** The run held for the opening, as the strip priced it at the press. */
export interface HeldRun {
  spec: RunSpec
  moves: number
  est: number | null
}

interface OpeningState {
  /** An opening is going: a press of Explore that has not landed yet. */
  opening: boolean
  /** The run pressed meanwhile, or null. */
  held: HeldRun | null
}

let state: OpeningState = { opening: false, held: null }
const watchers = new Set<() => void>()

function set(next: OpeningState): void {
  state = next
  for (const w of watchers) w()
}

/** Explore was pressed: an opening is going, and nothing is held yet. */
export function openingBegan(): void {
  set({ opening: true, held: null })
}

/**
 * The opening is over — landed, failed or stopped. The held run, if any, is handed
 * to the one caller that decides whether it starts, and the hold is cleared.
 */
export function openingSettled(): HeldRun | null {
  const held = state.held
  set({ opening: false, held: null })
  return held
}

/** The strip's Run, pressed while the opening is going. */
export function holdRun(run: HeldRun): void {
  if (!state.opening) return
  set({ opening: true, held: run })
}

/** Cancel, beside the held run. */
export function dropHeldRun(): void {
  if (!state.held) return
  set({ opening: state.opening, held: null })
}

function subscribe(fn: () => void): () => void {
  watchers.add(fn)
  return () => watchers.delete(fn)
}

const read = (): OpeningState => state

export function useOpening(): OpeningState {
  return useSyncExternalStore(subscribe, read, read)
}
